import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue";
const _withScopeId = n => (_pushScopeId("data-v-7e1d244b"), n = n(), _popScopeId(), n);
const _hoisted_1 = {
  key: 0,
  class: "back-top__text"
};
import { computed, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref } from 'vue';
import { VerticalAlignTopOutlined } from '@ant-design/icons-vue';
export default /*#__PURE__*/_defineComponent({
  __name: 'BackTop',
  props: {
    threshold: {
      default: 200
    },
    text: {
      default: '置顶'
    },
    right: {
      default: 32
    },
    bottom: {
      default: 48
    },
    target: {
      default: null
    }
  },
  emits: ["click"],
  setup(__props, {
    emit
  }) {
    const props = __props;
    /**
     * 一键置顶 / 回到顶部 悬浮按钮（公共组件）
     *
     * 特性：
     * - 自动探测最近的可纵向滚动祖先，兼容「页面级(window)滚动」和「容器滚动」两种场景，
     *   无需使用方手动指定滚动容器
     * - 滚动超过 threshold 才显示，带淡入淡出过渡
     * - 点击平滑滚回顶部，并 emit('click') 供使用方自行上报埋点（组件不耦合业务埋点）
     *
     * 用法：
     *   <BackTop @click="() => trackElement('一键置顶')" />
     *   <BackTop :threshold="300" text="" :bottom="80" />
     */
    const visible = ref(false);
    /** 锚点节点（0 尺寸、不受 v-show 影响、非 fixed），用作自动探测滚动容器的起点 */
    const anchorEl = ref(null);
    /**
     * 最近一次真正派发 scroll 事件的元素（即真实滚动容器）。
     * 由 handleScroll 记录，点击回顶时优先滚它——
     * 与"控制按钮显隐"用的是同一个滚动源，避免显隐与回顶目标不一致。
     */
    let lastScroller = null;
    const posStyle = computed(() => ({
      right: `${props.right}px`,
      bottom: `${props.bottom}px`
    }));
    /** 解析使用方传入的 target */
    const resolveTarget = () => {
      if (!props.target) return null;
      if (typeof props.target === 'string') return document.querySelector(props.target);
      return props.target;
    };
    /** 从 el 向上查找最近的可纵向滚动祖先，找不到则返回 window */
    const findScrollParent = el => {
      let node = (el === null || el === void 0 ? void 0 : el.parentElement) || null;
      while (node) {
        const {
          overflowY
        } = getComputedStyle(node);
        if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && node.scrollHeight > node.clientHeight) {
          return node;
        }
        node = node.parentElement;
      }
      return window;
    };
    /**
     * 解析当前实际的滚动容器，优先级：
     * 1) 使用方显式指定的 target
     * 2) 最近一次真正滚动的元素（lastScroller）—— 与按钮显隐同源，最可靠
     * 3) 兜底：从锚点向上探测可滚动祖先（首次加载、用户还没滚过时用）
     * 点击时才调用，此时 DOM 已稳定，探测结果可靠。
     */
    const resolveScrollContainer = () => {
      var _ref, _resolveTarget;
      return (_ref = (_resolveTarget = resolveTarget()) !== null && _resolveTarget !== void 0 ? _resolveTarget : lastScroller) !== null && _ref !== void 0 ? _ref : findScrollParent(anchorEl.value);
    };
    /**
     * 滚动监听（捕获阶段挂在 window 上）。
     * scroll 事件不冒泡，但捕获阶段能收到任意子容器的滚动，
     * 因此无需在挂载时就确定「谁在滚动」，容器/window 两种场景都覆盖。
     * 同时记录真实滚动源，供点击回顶复用（显隐与回顶目标一致）。
     */
    const handleScroll = e => {
      const target = e.target;
      let top = 0;
      if (target === document || target === window || target === document.documentElement) {
        lastScroller = window;
        top = window.scrollY || document.documentElement.scrollTop || 0;
      } else if (target instanceof HTMLElement) {
        lastScroller = target;
        top = target.scrollTop;
      }
      visible.value = top > props.threshold;
    };
    /**
     * 平滑滚动到顶部。
     * 不依赖浏览器对容器的 `behavior:'smooth'` 支持（部分容器/场景会被忽略，导致"点了没反应"），
     * 用 requestAnimationFrame 逐帧缓动，容器与 window 两种滚动对象都可靠生效。
     */
    const smoothScrollTop = container => {
      const isWindow = container === window;
      const getTop = () => isWindow ? window.scrollY || document.documentElement.scrollTop || 0 : container.scrollTop;
      const setTop = v => isWindow ? window.scrollTo(0, v) : container.scrollTop = v;
      const start = getTop();
      if (start <= 0) return;
      const duration = 300;
      let startTime = null;
      const step = now => {
        if (startTime == null) startTime = now;
        const progress = Math.min((now - startTime) / duration, 1);
        // easeOutCubic 缓动
        const eased = 1 - Math.pow(1 - progress, 3);
        setTop(Math.round(start * (1 - eased)));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const handleClick = () => {
      smoothScrollTop(resolveScrollContainer());
      emit('click');
    };
    // 捕获阶段 + passive：覆盖页面级与任意容器级滚动，且不阻塞滚动性能
    // addEventListener 对相同 listener+type+capture 自动去重，onMounted/onActivated 首次同时触发无副作用
    const addListener = () => window.addEventListener('scroll', handleScroll, {
      capture: true,
      passive: true
    });
    const removeListener = () => window.removeEventListener('scroll', handleScroll, {
      capture: true
    });
    onMounted(addListener);
    onBeforeUnmount(removeListener);
    // 兼容 keep-alive：页面被缓存（deactivated）时解绑，避免后台实例仍响应滚动、
    // 甚至把固定定位的按钮渲染到无关页面上；切回（activated）时重新绑定
    onActivated(addListener);
    onDeactivated(() => {
      removeListener();
      visible.value = false;
    });
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock("span", {
        ref_key: "anchorEl",
        ref: anchorEl,
        class: "back-top-anchor"
      }, [_createVNode(_Transition, {
        name: "back-top-fade"
      }, {
        default: _withCtx(() => [_withDirectives(_createElementVNode("button", {
          class: "back-top",
          style: _normalizeStyle(_unref(posStyle)),
          type: "button",
          title: "回到顶部",
          "aria-label": "回到顶部",
          onClick: handleClick
        }, [_createVNode(_unref(VerticalAlignTopOutlined), {
          class: "back-top__icon"
        }), __props.text ? (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(__props.text), 1)) : _createCommentVNode("", true)], 4), [[_vShow, visible.value]])]),
        _: 1
      })], 512);
    };
  }
});